<template>
    <div class="last-orders">
        <div class="lo-header">
            <h2>Ostatnie zamówienia</h2>
            <button class="btn btn-submit" @click="$router.push({ path: '/cms/orders' })">
                Zobacz wszystkie
            </button>
        </div>

        <div class="lo-list" v-if="lastOrders.length">
            <div v-for="o in lastOrders" :key="o.id" class="lo-item">
                <div class="lo-date">{{ orderStore.formatDate(o.created_at) }}</div>

                <div class="lo-trailer lo-ellipsis" :title="getTrailerName(o.trailer_id)">
                    {{ getTrailerName(o.trailer_id) }}
                </div>

                <div class="lo-customer lo-ellipsis" :title="orderStore.formatType(o.customer_type)">
                    {{ orderStore.formatType(o.customer_type) }}
                </div>

                <div class="lo-total">{{ formatPLN(orderTotal(o)) }}</div>

                <button class="btn btn-submit lo-btn" @click="viewOrder(o.id)">
                    Szczegóły
                </button>
            </div>
        </div>

        <div class="empty-state" v-else>
            <p>Brak zamówień.</p>
        </div>

        <teleport to="body">
            <transition name="fade">
                <OrderDetails v-if="selectedOrder && selectedOrder.trailer" :order="selectedOrder"
                    @close="selectedOrder = null" />
            </transition>
        </teleport>
    </div>
</template>

<script>
import { useOrderStore } from '@/stores/orderStore'
import { useTrailerStore } from '@/stores/trailerStore'
import { useAddonForModelStore } from '@/stores/addonForModelStore'
import OrderDetails from '@/components/cms/OrderDetails.vue'

export default {
    components: { OrderDetails },
    data() {
        return {
            orderStore: useOrderStore(),
            trailerStore: useTrailerStore(),
            afmStore: useAddonForModelStore(),
            selectedOrder: null
        }
    },
    computed: {
        orders() {
            return this.orderStore.orders || []
        },
        trailerMap() {
            const map = new Map()
            for (const t of this.trailerStore.trailers) map.set(t.id, t)
            return map
        },
        afmPriceIndex() {
            const idx = new Map()
            for (const r of this.afmStore.addonsForModels) {
                const key = `${r.trailer_id}|${r.addon_id}`
                const val = Math.round(parseFloat(r.price || 0) * 100)
                idx.set(key, val)
            }
            return idx
        },
        lastOrders() {
            return [...this.orders]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 8)
        }
    },
    methods: {
        getTrailerName(id) {
            return this.trailerMap.get(id)?.name || '—'
        },
        orderTotal(order) {
            const trailerPrice = this.trailerMap.get(order.trailer_id)?.price || 0
            const addonIds = order.addon_ids || []
            const addonsSum = addonIds.reduce((sum, aid) => {
                const key = `${order.trailer_id}|${aid}`
                return sum + (this.afmPriceIndex.get(key) || 0)
            }, 0)
            return trailerPrice + addonsSum
        },
        formatPLN(cents) {
            return (cents / 100).toLocaleString('pl-PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: 2
            })
        },
        viewOrder(id) {
            const order = this.orders.find(o => o.id === id)
            if (!order) return

            const trailer = this.trailerStore.trailers.find(t => t.id === order.trailer_id) || null
            const addons = this.trailerStore.addons?.filter(a => order.addon_ids.includes(a.id)) || []

            const enrichedAddons = addons.map(addon => {
                const price = this.afmStore.addonsForModels.find(
                    afm => afm.addon_id === addon.id && afm.trailer_id === trailer?.id
                )?.price

                return {
                    ...addon,
                    group: addon.group || 'Inne',
                    price: Math.round(parseFloat(price || 0) * 100)
                }
            })

            this.selectedOrder = {
                ...order,
                trailer,
                addons: enrichedAddons
            }
        }
    },
    created() {
        if (!this.orderStore.orders?.length) this.orderStore.fetchOrders?.()
        if (!this.trailerStore.trailers?.length) this.trailerStore.fetchTrailers?.()
        if (!this.afmStore.addonsForModels?.length) this.afmStore.fetchAll?.()
    }
}
</script>

<style scoped lang="scss">
.last-orders {
    background: $color-white;
    border: 1px solid $color-grey;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 2rem;
}

.lo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.lo-list {
    display: flex;
    flex-direction: column;
}

.lo-item {
    display: grid;
    grid-template-columns: 110px minmax(160px, 1fr) minmax(120px, 0.7fr) 100px 100px;
    gap: 0.5rem;
    align-items: center;
    padding: 0.6rem 0;
    border-bottom: 1px dashed $color-grey;
    min-height: 48px;
}

.lo-item:last-child {
    border-bottom: 0;
}

.lo-date {
    font-size: 0.9rem;
    color: $color-text-light;
    white-space: nowrap;
}

.lo-ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.lo-trailer {
    font-weight: 600;
    color: $color-text;
}

.lo-customer {
    color: $color-text-light;
}

.lo-total {
    text-align: right;
    font-weight: 600;
    color: $color-primary-dark;
    font-variant-numeric: tabular-nums;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: 0.2s;
}

.btn-submit {
    background: $color-primary;
    color: #fff;
}

.btn-submit:hover {
    background: $color-primary-dark;
}

.lo-btn {
    justify-self: end;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 1000px) {
    .last-orders {
        padding: 16px;
    }

    .lo-item {
        grid-template-columns: 1fr auto;
        grid-template-areas:
            "date   total"
            "trailer btn"
            "customer btn";
        align-items: start;
        gap: 0.4rem 0.75rem;
        padding: 12px 0;
    }

    .lo-date {
        grid-area: date;
        font-size: 0.9rem;
    }

    .lo-trailer {
        grid-area: trailer;
    }

    .lo-customer {
        grid-area: customer;
    }

    .lo-total {
        grid-area: total;
        text-align: right;
    }

    .lo-btn {
        grid-area: btn;
        justify-self: end;
        align-self: start;
    }

    .btn {
        padding: 0.6rem 1rem;
    }
}
</style>